/**
 * <scroll-reveal> — the theme's only motion-on-scroll primitive.
 *
 * Usage: <scroll-reveal preset="fade-up" stagger="80">…children…</scroll-reveal>
 *   - preset:  key from PRESETS (below). Default "fade-up".
 *   - stagger: ms between direct children (0 = reveal as one). Default 0.
 *   - once:    present = reveal once; absent = also un-reveal when scrolled out.
 *
 * Rules (see docs/SPEC.md §4):
 *   - Content is NEVER hidden unless <html class="js"> is present (set in theme.liquid),
 *     so crawlers and no-JS users get everything.
 *   - prefers-reduced-motion: reduce → no transform, opacity only, short duration.
 *   - Pure CSS transitions driven by custom properties; this file only toggles state.
 */

// ───────────────────────────────────────────────────────────────────────────
// TODO(Chris): define the preset table. This is the "premium" taste decision:
// which motions exist, how far they travel, how long they take, what easing.
// Each preset = CSS custom property values the component sets on the element.
//   --reveal-x / --reveal-y : starting translate (px or %), e.g. "0" / "24px"
//   --reveal-scale          : starting scale, e.g. "0.96"
//   --reveal-opacity        : starting opacity, e.g. "0"
//   --reveal-duration       : e.g. "600ms"
//   --reveal-ease           : e.g. "cubic-bezier(.2,.7,.2,1)"
// Suggested set: fade-up (default), fade, rise (bigger travel, hero only),
// scale-in (cards/media), slide-left/slide-right (editorial split sections),
// clip-up (text lines, uses --reveal-clip). Keep travel ≤ 32px and duration
// ≤ 700ms; longer reads as sluggish on mobile. One example is filled in so
// the component works today.
const PRESETS = {
  // Skins tune travel/duration via --reveal-y-default / --reveal-duration-default in skin.css.
  'fade-up': { '--reveal-y': 'var(--reveal-y-default, 20px)', '--reveal-opacity': '0', '--reveal-duration': 'var(--reveal-duration-default, 600ms)', '--reveal-ease': 'cubic-bezier(.2,.7,.2,1)' },
};
// ───────────────────────────────────────────────────────────────────────────

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

let observer;
// Reveal-once elements still waiting. The observer only reports when an element crosses the viewport edge, so a
// jump that carries an element from below the screen to above it (a fast flick, an anchor link, a restored scroll
// position) never fires it. A cheap scroll sweep catches those: anything at or above the fold is revealed.
const pending = new Set();
let sweepQueued = false;

function reveal(el) {
  el.classList.add('is-revealed');
  pending.delete(el);
  observer?.unobserve(el);
}

function sweep() {
  sweepQueued = false;
  const fold = window.innerHeight * 1.15;
  for (const el of pending) if (el.getBoundingClientRect().top < fold) reveal(el);
}

function queueSweep() {
  if (sweepQueued || pending.size === 0) return;
  sweepQueued = true;
  requestAnimationFrame(sweep);
}

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target;
      const once = el.closest('scroll-reveal')?.hasAttribute('once');
      if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
        if (once) reveal(el);
        else el.classList.add('is-revealed');
      } else if (!once) {
        el.classList.remove('is-revealed');
      }
    }
  }, { rootMargin: '0px 0px 15% 0px', threshold: 0 }); // starts just below the fold so content is in place as it arrives
  window.addEventListener('scroll', queueSweep, { passive: true });
  window.addEventListener('scrollend', queueSweep, { passive: true });
  return observer;
}

class ScrollReveal extends HTMLElement {
  connectedCallback() {
    if (!document.documentElement.classList.contains('js')) return;
    const preset = PRESETS[this.getAttribute('preset')] || PRESETS['fade-up'];
    const stagger = Number(this.getAttribute('stagger') || 0);
    const targets = stagger > 0 ? Array.from(this.children) : [this];

    targets.forEach((el, i) => {
      for (const [prop, value] of Object.entries(preset)) el.style.setProperty(prop, value);
      if (REDUCED.matches) {
        el.style.setProperty('--reveal-x', '0');
        el.style.setProperty('--reveal-y', '0');
        el.style.setProperty('--reveal-scale', '1');
        el.style.setProperty('--reveal-duration', '200ms');
      }
      if (stagger > 0) el.style.setProperty('--reveal-delay', `${i * stagger}ms`);
      el.setAttribute('data-reveal', '');
      if (this.hasAttribute('once')) pending.add(el);
      getObserver().observe(el);
    });
  }
}

if (!customElements.get('scroll-reveal')) customElements.define('scroll-reveal', ScrollReveal);
